import { lazy, Suspense, useMemo } from "react";
import { ErrorBoundary } from "@sentry/react";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { ErrorFallback } from "../../patterns";
import { addTranslationResources } from "./DHSIntegrationSettings.translations";
import { DHSIntegrationSettingsLoading } from "./DHSIntegrationSettings.loading";
import { DHSIntegrationSettingsProps } from "./DHSIntegrationSettings.types";
import { useFeatureFlag } from "../FeatureFlag/FeatureFlagsProvider";

export const DHSIntegrationSettingsLoadable = lazy(
  () => import("./DHSIntegrationSettings")
);

type WrapperProps = Omit<DHSIntegrationSettingsProps, "i18next" | "useTranslation"> & {
  i18next?: typeof i18next;
};

// Resources are attached synchronously during render so the first paint already sees translations.
// i18next and react-i18next are externalized in vite.config.ts so lazy chunks resolve the host's singleton.
export const DHSIntegrationSettingsWrapper = (props: WrapperProps) => {
  const useTranslationFn = useTranslation;
  const i18nInstance = props.i18next || i18next;
  const dhsEnabled = useFeatureFlag("FEATURE_DHS_INTEGRATION");
  const { user } = props;

  useMemo(() => {
    addTranslationResources(i18nInstance);
  }, [i18nInstance]);

  // Returning null also keeps the lazy chunk from loading for non-DHS tenants.
  if (!dhsEnabled || !user?.permissions?.viewDhsIntegration) return null;

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<DHSIntegrationSettingsLoading />}>
        <DHSIntegrationSettingsLoadable
          {...(props as DHSIntegrationSettingsProps)}
          i18next={i18nInstance}
          useTranslation={useTranslationFn}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

export { DHSIntegrationSettingsWrapper as DHSIntegrationSettings }
